"use client";

import React, { useState } from "react";
import { CustomPagination } from "./custom-pagination";

interface TableColumn {
  key: string;
  label: string;
  render?: (value: any, row?: any) => React.ReactNode;
}
interface TableProps<T> {
  data: T[];
  columns: TableColumn[];
  total: number;
  page: number;
  setPage: (p: number) => void;
  actions?: (row: T) => React.ReactNode;
  isLoading?: boolean;
  emptyMessage?: string;
}

export default function DynamicTable<T>({
  data,
  columns,
  total,
  page,
  setPage,
  actions,
  isLoading = false,
  emptyMessage = "Nenhum registro encontrado.",
}: TableProps<T>) {
  function formatCNPJ(value: string): string {
    if (!value) return "";
    return value
      .replace(/\D/g, "")
      .replace(/^(\d{2})(\d)/, "$1.$2")
      .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/\.(\d{3})(\d)/, ".$1/$2")
      .replace(/(\d{4})(\d)/, "$1-$2")
      .slice(0, 18);
  }

  function getNestedValue(obj: any, path: string): any {
    return path.split(".").reduce((acc, part) => acc?.[part], obj);
  }

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", padding: 40, color: "#888" }}>
        Carregando dados...
      </div>
    );
  }
  if (data.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: 40, color: "#888" }}>
        {emptyMessage}
      </div>
    );
  }

  const wrapperStyle: React.CSSProperties = {
    borderRadius: 16,
    boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
  };
  const tableContainer: React.CSSProperties = {
    overflowX: "auto",
    marginBottom: 16,
  };
  const tableStyle: React.CSSProperties = {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: "0 8px",
  };
  const thStyle: React.CSSProperties = {
    textAlign: "left",
    padding: "2px 6px",
    color: "#666",
    fontWeight: 500,
    borderBottom: "1px solid #e0e0e0",
  };
  const trStyle: React.CSSProperties = {
    background: "#fafafa",
    borderRadius: 8,
    transition: "background-color 0.2s",
  };
  const tdStyle: React.CSSProperties = {
    padding: "2px 6px",
    color: "#333",
  };

  return (
    <div style={wrapperStyle}>
      <div style={tableContainer}>
        <table style={tableStyle}>
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.key} style={thStyle}>
                  {col.label}
                </th>
              ))}
              {actions && (
                <th style={{ ...thStyle, textAlign: "center" }}>Ações</th>
              )}
            </tr>
          </thead>
          <tbody>
            {data.map((row, idx) => (
              <tr
                key={idx}
                style={trStyle}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#eef3f7")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "#fafafa")
                }
              >
                {columns.map((col) => (
                  <td key={col.key} style={tdStyle}>
                    {(() => {
                      const value = getNestedValue(row, col.key);
                      const isCNPJ = col.key.toLowerCase().endsWith("cnpj");

                      if (col.render) {
                        return col.render(value, row);
                      }

                      return isCNPJ ? formatCNPJ(value) : value;
                    })()}
                  </td>
                ))}
                {actions && (
                  <td style={{ ...tdStyle, textAlign: "center" }}>
                    {actions(row)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <CustomPagination
          currentPage={page}
          totalPages={total}
          onPageChange={setPage}
        />
      </div>
    </div>
  );
}
